/**
 * TimeEntryReview Component
 *
 * Admin batch review of submitted time entries grouped by contractor.
 * Supports select all, approve/reject with optional notes.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TimeEntry } from '@/types/contractors';
import { timeEntriesApi } from '@/services/contractorsApi';

interface GroupedEntries {
  contractorName: string;
  contractorUserId: number;
  entries: TimeEntry[];
}

export function TimeEntryReview(): React.JSX.Element {
  const { t } = useTranslation('contractors');

  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await timeEntriesApi.list({ status: 'submitted', limit: 200 });
      setEntries(Array.isArray(result) ? result : []);
    } catch {
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchEntries();
  }, [fetchEntries]);

  // Group entries by contractor
  const grouped: GroupedEntries[] = [];
  const contractorMap = new Map<number, GroupedEntries>();
  for (const entry of entries) {
    let group = contractorMap.get(entry.contractor_user_id);
    if (!group) {
      group = {
        contractorName: entry.contract_title ?? `Contractor #${entry.contractor_user_id}`,
        contractorUserId: entry.contractor_user_id,
        entries: [],
      };
      contractorMap.set(entry.contractor_user_id, group);
      grouped.push(group);
    }
    group.entries.push(entry);
  }

  const handleSelectToggle = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = (): void => {
    if (selectedIds.size === entries.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(entries.map((e) => e.id)));
    }
  };

  const handleReview = async (action: 'approve' | 'reject'): Promise<void> => {
    if (selectedIds.size === 0) return;
    setProcessing(true);
    setError(null);
    try {
      await timeEntriesApi.review({
        entry_ids: Array.from(selectedIds),
        action,
        notes: reviewNotes || undefined,
      });
      setSelectedIds(new Set());
      setReviewNotes('');
      await fetchEntries();
    } catch {
      setError(t('errors.reviewFailed'));
    } finally {
      setProcessing(false);
    }
  };

  const totalHours = entries
    .filter((e) => selectedIds.has(e.id))
    .reduce((sum, e) => sum + e.hours, 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{t('timeReview.title')}</h2>
        <span className="text-sm text-gray-500">
          {t('timeReview.pendingCount', { count: entries.length })}
        </span>
      </div>

      {error && (
        <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">{error}</div>
      )}

      {/* Action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
          <span className="text-sm font-medium text-blue-800">
            {t('timeReview.selectedCount', { count: selectedIds.size, hours: totalHours })}
          </span>
          <div className="flex-1">
            <input
              type="text"
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder={t('timeReview.notesPlaceholder')}
              className="w-full px-3 py-1.5 text-sm border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => handleReview('approve')}
            disabled={processing}
            className="px-4 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {t('timeReview.approve')}
          </button>
          <button
            onClick={() => handleReview('reject')}
            disabled={processing}
            className="px-4 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {t('timeReview.reject')}
          </button>
        </div>
      )}

      {/* Select All */}
      {entries.length > 0 && (
        <div className="flex items-center gap-2 px-1">
          <input
            type="checkbox"
            checked={selectedIds.size === entries.length && entries.length > 0}
            onChange={handleSelectAll}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600">{t('common.selectAll')}</span>
        </div>
      )}

      {/* Loading / Empty */}
      {isLoading ? (
        <div className="py-12 text-center text-sm text-gray-500">{t('common.loading')}</div>
      ) : entries.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500">{t('timeReview.noSubmitted')}</div>
      ) : (
        /* Grouped entries */
        <div className="space-y-6">
          {grouped.map((group) => (
            <div key={group.contractorUserId} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-800">{group.contractorName}</h3>
                <span className="text-xs text-gray-500">
                  {group.entries.length} {t('timeReview.entries')} - {group.entries.reduce((s, e) => s + e.hours, 0)}h
                </span>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left w-10"></th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('timeEntries.date')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('timeEntries.hours')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('timeEntries.project')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('timeEntries.description')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {group.entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(entry.id)}
                          onChange={() => handleSelectToggle(entry.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">{entry.date}</td>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{entry.hours}h</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{entry.project_name || '-'}</td>
                      <td className="px-4 py-2 text-sm text-gray-600 max-w-sm truncate">{entry.description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
